import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
  Typography,
  Button,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE;

export default function VisitorForm({ userid }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: '',
    gender: 'Male',
    mobile: '',
    aadhar: '',
    epic_no: '',
    vidhansabha: 'Karad South',
    karad: { village: '', ganName: '', zpName: '', reference: '' },
    other: { village: '', taluka: '', district: '' },
    booth_no: '',
    purpose: '',
    status: 'Green',
    remarks: '',
  });
  const [editing, setEditing] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    village: '',
    ganName: '',
    zpName: '',
    reference: '',
    purpose: '',
    status: ''
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // fetch records on mount
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-set remarks based on status
  useEffect(() => {
    if (form.status === 'Green') setForm((f) => ({ ...f, remarks: 'Documents Ok' }));
    if (form.status === 'Yellow') setForm((f) => ({ ...f, remarks: 'Partial Documents Ok. We can call him/her' }));
    if (form.status === 'Red') setForm((f) => ({ ...f, remarks: 'Reason - have some issues' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.status]);

  async function fetchRecords() {
    try {
      const res = await fetch(`${API_BASE}/visitors`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRecords((data || []).map(mapFromApi));
    } catch (err) {
      console.error('fetchRecords:', err);
    }
  }

  function mapFromApi(a) {
    return {
      id: a.id,
      name: a.name,
      gender: a.gender,
      mobile: a.mobile,
      aadhar: a.aadhar,
      epic_no: a.epic_no,
      vidhansabha: a.vidhansabha_name || '',
      karad: {
        village: a.village || '',
        ganName: a.gan_name || '',
        zpName: a.zp_name || '',
        reference: a.reference || '',
      },
      other: {
        village: a.village || '',
        taluka: a.taluka || '',
        district: a.district || '',
      },
      booth_no: a.booth_no,
      purpose: a.purpose,
      status: a.status,
      remarks: a.remarks,
      created_at: a.created_at,
    };
  }

  function mapToApi(f, uid) {
    return {
      userid: uid,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      epic_no: f.epic_no || null,
      vidhansabha_name: f.vidhansabha,
      village: f.vidhansabha === 'Karad South' ? f.karad.village : f.other.village,
      booth_no: f.booth_no || null,
      gan_name: f.karad.ganName || null,
      zp_name: f.karad.zpName || null,
      reference: f.karad.reference || null,
      taluka: f.other.taluka || null,
      district: f.other.district || null,
      purpose: f.purpose,
      status: f.status,
      remarks: f.remarks,
    };
  }

  function handleChange(e) {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const key = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [key]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const key = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [key]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    (async () => {
      try {
        if (editing) {
          const res = await fetch(`${API_BASE}/visitors/${form.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mapToApi(form, userid)),
          });
          if (!res.ok) throw new Error('Update failed');
        } else {
          const res = await fetch(`${API_BASE}/visitors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mapToApi(form, userid)),
          });
          if (!res.ok) throw new Error('Create failed');
        }
        await fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } catch (err) {
        console.error('handleSubmit:', err);
        alert('Save failed');
      }
    })();
  }

  function resetForm() {
    setForm({
      id: null,
      name: '',
      gender: 'Male',
      mobile: '',
      aadhar: '',
      epic_no: '',
      vidhansabha: 'Karad South',
      karad: { village: '', ganName: '', zpName: '', reference: '' },
      other: { village: '', taluka: '', district: '' },
      booth_no: '',
      purpose: '',
      status: 'Green',
      remarks: 'Documents Ok',
    });
    setEditing(false);
    setDrawerOpen(false);
  }

  function handleEdit(id) {
    const rec = records.find((r) => r.id === id);
    if (rec) {
      setForm({ ...rec });
      setEditing(true);
      setDrawerOpen(true);
    }
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this record?')) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/visitors/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Delete failed');
        await fetchRecords();
      } catch (err) {
        console.error('handleDelete:', err);
        alert('Delete failed');
      }
    })();
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }

  const filteredRecords = records.filter((r) => {
    return (
      (r.name || '').toLowerCase().includes(filters.name.toLowerCase()) &&
      (r.mobile || '').toLowerCase().includes(filters.mobile.toLowerCase()) &&
      (r.aadhar || '').toLowerCase().includes(filters.aadhar.toLowerCase()) &&
      (r.karad?.village || r.other?.village || '').toLowerCase().includes(filters.village.toLowerCase()) &&
      (r.karad?.ganName || '').toLowerCase().includes(filters.ganName.toLowerCase()) &&
      (r.karad?.zpName || '').toLowerCase().includes(filters.zpName.toLowerCase()) &&
      (r.karad?.reference || '').toLowerCase().includes(filters.reference.toLowerCase()) &&
      (r.purpose || '').toLowerCase().includes(filters.purpose.toLowerCase()) &&
      (r.status || '').toLowerCase().includes(filters.status.toLowerCase())
    );
  });

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', fontWeight: 'bold' }}>Visitor Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Visitor
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Mobile', 'Adhar No', 'Village', 'Gan Name', 'Zp Name', 'Reference', 'Purpose', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="village" value={filters.village} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="ganName" value={filters.ganName} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="zpName" value={filters.zpName} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="reference" value={filters.reference} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="purpose" value={filters.purpose} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="status" value={filters.status} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((r) => (
                <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                  <TableCell>{r.mobile}</TableCell>
                  <TableCell>{r.aadhar}</TableCell>
                  <TableCell>{r.karad?.village || r.other?.village}</TableCell>
                  <TableCell>{r.karad?.ganName}</TableCell>
                  <TableCell>{r.karad?.zpName}</TableCell>
                  <TableCell>{r.karad?.reference}</TableCell>
                  <TableCell>{r.purpose}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.status}
                      sx={{
                        bgcolor: r.status === 'Green' ? '#4caf50' : r.status === 'Yellow' ? '#fbc02d' : '#f44336',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 450 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Visitor' : 'Add New Visitor'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />
            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="EPIC No" name="epic_no" value={form.epic_no} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.vidhansabha === 'Karad South' ? (
              <Box sx={{ borderLeft: '3px solid #00acc1', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="karad.village" value={form.karad.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Gan Name" name="karad.ganName" value={form.karad.ganName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="ZP Name" name="karad.zpName" value={form.karad.zpName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Reference" name="karad.reference" value={form.karad.reference} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            ) : (
              <Box sx={{ borderLeft: '3px solid #8e24aa', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="other.village" value={form.other.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            )}

            <TextField label="Taluka" name="other.taluka" value={form.other.taluka} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="District" name="other.district" value={form.status === 'Green' ? 'Satara' : form.other.district} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="purpose-label">Purpose of Visit</InputLabel>
              <Select labelId="purpose-label" label="Purpose of Visit" name="purpose" value={form.purpose} onChange={handleChange}>
                <MenuItem value="Enquiry">Enquiry</MenuItem>
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Re-new">Re-new</MenuItem>
                <MenuItem value="Claim">Claim</MenuItem>
                <MenuItem value="Nominee">Nominee</MenuItem>
                <MenuItem value="Appointment">Appointment</MenuItem>
                <MenuItem value="Household">Household</MenuItem>
                <MenuItem value="Essential">Essential</MenuItem>
                <MenuItem value="Safety">Safety</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Enquiry Status</Typography>
              <RadioGroup row name="status" value={form.status} onChange={handleChange}>
                <FormControlLabel value="Green" control={<Radio color="success" />} label={<Chip label="Green" sx={{ bgcolor: '#4caf50', color: 'white' }} size="small" />} />
                <FormControlLabel value="Yellow" control={<Radio sx={{ color: '#fbc02d', '&.Mui-checked': { color: '#fbc02d' } }} />} label={<Chip label="Yellow" sx={{ bgcolor: '#fbc02d', color: 'white' }} size="small" />} />
                <FormControlLabel value="Red" control={<Radio color="error" />} label={<Chip label="Red" sx={{ bgcolor: '#f44336', color: 'white' }} size="small" />} />
              </RadioGroup>
            </Box>

            <TextField label="Remarks" name="remarks" value={form.remarks} onChange={handleChange} multiline rows={2} fullWidth variant="outlined" />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }}>
              {editing ? 'Update Record' : 'Save Record'}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
